'use client'

import { useMemo } from 'react'
import { sortBy, uniqBy } from 'lodash'
import type { LabelWithMembers, Team } from '@skop-app/lib-domain/types'

import { useUserContext } from '@/context'
import { useOrganizationTree } from '@/services/queries/team'

const buildOrganizationTeams = (source?: Team | null): LabelWithMembers[] => {
  if (!source?.id) return []

  const items = [
    {
      label: source.name,
      value: source.id,
      members: source.members,
    },
    ...(source.teams ?? []).map((team) => ({
      label: team.name,
      value: team.id,
      members: team.members,
    })),
  ]

  return sortBy(uniqBy(items, 'value'), 'label')
}

export function useSelectedOrganizationTeams() {
  const { organizationScope } = useUserContext()
  const selectedOrganizationParent =
    organizationScope.selectedOrganizationParent

  const currentTreeQuery = useOrganizationTree(
    organizationScope.selectedOrganizationId,
  )
  const {
    data: currentTree,
    isLoading: isLoadingCurrent,
    isFetching: isFetchingCurrent,
    isError: isErrorCurrent,
    isSuccess: isSuccessCurrent,
  } = currentTreeQuery

  const shouldUseParentTree =
    Boolean(selectedOrganizationParent?.id) &&
    Boolean(currentTree?.id) &&
    currentTree?.teams?.length === 0 &&
    selectedOrganizationParent?.partner !== 'cyneo'

  const parentTreeQuery = useOrganizationTree(
    shouldUseParentTree ? selectedOrganizationParent?.id : null,
  )
  const {
    data: parentTree,
    isLoading: isLoadingParent,
    isFetching: isFetchingParent,
    isError: isErrorParent,
  } = parentTreeQuery

  const scopeTeam = shouldUseParentTree ? parentTree : currentTree

  const data = useMemo(() => buildOrganizationTeams(scopeTeam), [scopeTeam])

  const isLoading =
    (isLoadingCurrent && !currentTree) ||
    (shouldUseParentTree && isLoadingParent && !parentTree)
  const isError = isErrorCurrent || (shouldUseParentTree && isErrorParent)
  const isFetching =
    isFetchingCurrent || (shouldUseParentTree && isFetchingParent)
  const readState = isError ? 'error' : isLoading ? 'loading' : 'ready'
  const isEmpty =
    isSuccessCurrent &&
    !isError &&
    (!shouldUseParentTree || Boolean(parentTree)) &&
    data.length === 0

  return {
    data,
    scopeTeam,
    isLoading,
    isFetching,
    isError,
    isEmpty,
    readState,
  }
}
